// Parent Mode > Settings (docs/FILE_SPLIT_RUN1_SPEC.md, run 1): the menu, the category detail and
// SettingsTab itself stay here; the cards now live under ./settings/ and are re-exported unchanged below.

import { Icon } from "./Common.jsx";
import { NotificationToggle, usePushStatus } from "./Onboarding.jsx";
import { MAX_EXCHANGE_RATE, tokenValueParts } from "../lib/constants.js";
import { staleVaultChildren } from "../lib/vaultInterest.js";
import { t as ct } from "../lib/content.js";
import { useBackLayer } from "../lib/backstack.js";
import { TokenSetupFields, VaultInterestCard, DoubleTokenDayCard, AdaptiveCard } from "./settings/Money.jsx";
import { HouseholdManagementSection, HouseholdCoverageCard } from "./settings/Household.jsx";
import { DevicesCard } from "./settings/Devices.jsx";
import { SecuritySection, DangerZoneSection } from "./settings/Security.jsx";
import { NotificationGroup, TaskReminderCard } from "./settings/Notifications.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// MAX_EXCHANGE_RATE and tokenValueParts live in lib/constants.js (Alan, Aug 2026).
//
// They were briefly defined here, which quietly made this JSX module a test dependency:
// 23-token-value.spec.js imported them, Node loaded SettingsTab.jsx -> Common.jsx, and
// Common.jsx destructures a bare global React at module scope. Playwright aborts collecting
// EVERY spec when one throws, so the whole suite died on "React is not defined" — the exact
// crash the ACTIVITY_KIND_STYLE move had just fixed, reached by a second route.
//
// Rule of thumb this leaves behind: anything a test might want to assert on belongs in a lib
// module, not in a component file. Re-exported here so component-side importers are unchanged.
export { MAX_EXCHANGE_RATE, tokenValueParts };

/* -------------------------------- settings menu ------------------------------ */

// Single source of truth for what categories exist and in what order — adding a future
// category (Backups, Appearance, whatever comes next) means adding one entry here, not
// touching the menu layout or the detail-screen routing below. `danger: true` gives a row
// the red treatment in the menu itself, not just once you're inside it, so severity is
// visible before tapping in.
const SETTINGS_CATEGORIES = [
  { id: "rewards", iconId: "ic-banknote", label: "Money", description: "Token value, Money Monday interest, double token day" },
  // LB-265 (Alan, 12 Sep): "Move Learning Coach to a new Premium section in settings", named
  // Learning on his call. It is not a money setting, so a parent looking for it had no reason
  // to open Money — and it is the one thing we charge for, which needs a home of its own for
  // LB-67's upgrade surfaces to live in rather than the exchange-rate screen. Second in the
  // list: money-adjacent, and high enough to be seen.
  { id: "learning", iconId: "ic-star", label: "Learning", description: "Learning Coach · free in the Preview" },
  { id: "notifications", iconId: "ic-bell", label: "Notifications", description: "Request alerts and task reminders" },
  { id: "household", iconId: "ic-home", label: "Household", description: "Joining a device, code and passcode, owner name and email" },
  { id: "devices", iconId: "ic-devices", label: "Devices", description: "Rename or remove linked devices" },
  { id: "security", iconId: "ic-pin", label: "Security and history", description: "Parent PIN · audit log" },
  { id: "danger", iconId: "ic-attention", label: "Data and privacy", description: "Your data, privacy policy, deletion", danger: true },
];

function categoryNeedsAttention(id, state, pushStatus) {
  // IA-approved dot addition (review 2.1): also flags an owner email that's set but
  // never clicked-to-verify, alongside the existing no-email-on-file case. The dot's
  // meaning stays "something needs fixing" either way.
  if (id === "household") return !state.ownerEmail || !state.ownerEmailVerified;
  if (id === "security") return !state.settings.pinConfigured;
  // A parent with notifications off will miss every request until they happen to open the
  // app — the highest-consequence unset thing in here, and previously the only one with no
  // signal at all pointing at it. "unsupported"/"checking" deliberately don't flag: there's
  // nothing the parent could do about either.
  if (id === "notifications") return pushStatus === "off" || pushStatus === "denied" || pushStatus === "needs-install";
  // The "currency changed but interest never set" class of condition this comment always
  // promised: with interest on, a vault balance nobody has touched in STALE_VAULT_DAYS is
  // probably out of date with reality — and the parent is paying interest on the drift.
  // See staleVaultChildren (lib/vaultInterest.js) for the exact rule.
  if (id === "rewards") return staleVaultChildren(state).length > 0;
  return false;
}

function SettingsMenu({ state, onSelect }) {
  const { status: pushStatus } = usePushStatus();
  // LB-164 (pass K): the categories are one white list of rows — icon disc, label, one-line
  // description, chevron in ink — rather than a stack of cards. The data & privacy row is
  // rose text with a rose disc; the tinted card is gone.
  return (
    <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200 lb-form">
      {SETTINGS_CATEGORIES.map((cat) => {
        const attention = categoryNeedsAttention(cat.id, state, pushStatus);
        return (
          <button
            key={cat.id}
            type="button"
            onClick={() => onSelect(cat.id)}
            className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-slate-50"
          >
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${cat.danger ? "bg-rose-50" : "bg-slate-100"}`}>
              <Icon id={cat.iconId} size={18} hue={cat.danger ? "rose" : undefined} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${cat.danger ? "text-rose-600" : "text-slate-900"}`}>{cat.label}</div>
              <div className={`text-xs ${cat.danger ? "text-rose-500" : "text-slate-500"}`}>{cat.description}</div>
            </div>
            {attention && <span className="w-2.5 h-2.5 rounded-full bg-rose-500 flex-shrink-0" aria-label="Needs attention" />}
            <Icon id="ic-forward" size={18} className={`flex-shrink-0 ${cat.danger ? "text-rose-400" : "text-slate-900"}`} style={{ color: "currentColor" }} />
          </button>
        );
      })}
    </div>
  );
}

function SettingsCategoryDetail({ category, onBack, children }) {
  return (
    <div className="flex flex-col gap-1.5 lb-form">
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-slate-500 font-bold text-sm mb-1 px-1 self-start">
        <Icon id="ic-back" size={16} className="inline-block align-[-3px] mr-1" style={{ color: "currentColor" }} />Settings
        <span className="text-slate-300 font-normal mx-1">/</span>
        <span className="text-slate-700"><Icon id={category.iconId} size={16} hue={category.danger ? "rose" : undefined} className="inline-block align-[-2px] mr-1" /> {category.label}</span>
      </button>
      {children}
    </div>
  );
}

/* -------------------------------- settings tab ------------------------------ */

export function SettingsTab({ state, update, showToast, onSpendingReview, code, verifiedPin, setPendingVerifiedPin, setPendingVerifiedPasscode, setPendingVerifiedJoinToken, onLeaveHousehold, initialCategory, onCategoryChange, viaDeepLink, onExitSettings , onAwaitingJoin}) {
  const [activeCategory, setActiveCategory] = useState(initialCategory || null);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  // Bumped by NotificationToggle's onStatusChange every time this device's subscription
  // actually changes (enable or disable) — HouseholdCoverageCard depends on this to know
  // when to re-fetch, since otherwise it only ever fetched once on mount (see its own
  // comment). A plain counter rather than the new status string itself: the card cares
  // "something changed, go check the server again", not what the new local status is.
  const [pushRefreshKey, setPushRefreshKey] = useState(0);

  // Kept in step with the parent's copy so a deep link (Requests → "Turn notifications on")
  // lands on the right category, and so backing out to the menu clears it — otherwise a
  // second visit to Settings would keep re-opening the category that was linked to once.
  function selectCategory(next) {
    setActiveCategory(next);
    if (onCategoryChange) onCategoryChange(next);
  }

  useEffect(() => {
    if (initialCategory && initialCategory !== activeCategory) setActiveCategory(initialCategory);
  }, [initialCategory]);
  // LB-315: a category is one layer over the Settings menu (back = the menu's ‹ Back); the
  // Leave household confirmation is one more on top of it.
  useBackLayer(!!activeCategory, () => selectCategory(null));
  useBackLayer(confirmingLeave, () => setConfirmingLeave(false));

  if (!activeCategory) {
    return <SettingsMenu state={state} onSelect={selectCategory} />;
  }

  const category = SETTINGS_CATEGORIES.find((c) => c.id === activeCategory);

  // Arrived via a deep link (household-money row, Requests' notifications banner): one
  // tap on the category breadcrumb exits Settings entirely rather than dropping back to
  // the category menu — there's nowhere useful to browse to first, and leaving the normal
  // drill-up behaviour in place here is exactly what left parents feeling stuck.
  return (
    <SettingsCategoryDetail category={category} onBack={viaDeepLink && onExitSettings ? onExitSettings : () => selectCategory(null)}>
      <SettingsErrorBoundary onBack={() => selectCategory(null)}>
      {activeCategory === "household" && (
        <HouseholdManagementSection
          onAwaitingJoin={onAwaitingJoin}
          state={state}
          update={update}
          showToast={showToast}
          code={code}
          verifiedPin={verifiedPin}
          setPendingVerifiedPasscode={setPendingVerifiedPasscode}
          setPendingVerifiedJoinToken={setPendingVerifiedJoinToken}
          onLeaveHousehold={onLeaveHousehold}
          confirmingLeave={confirmingLeave}
          setConfirmingLeave={setConfirmingLeave}
          onOpenCategory={setActiveCategory}
        />
      )}
      {activeCategory === "devices" && <DevicesCard code={code} showToast={showToast} pinToProve={state.settings.pinConfigured ? verifiedPin : "123456"} />}
      {/* LB-265 order (Alan, 12 Sep): token value, then vault interest, then double token
          day — the thing every household sets, then the thing most will set, then the thing
          that simply runs. Learning Coach left this category entirely. */}
      {activeCategory === "rewards" && (
        <>
          <TokenSetupFields state={state} update={update} showToast={showToast} onSpendingReview={onSpendingReview} />
          <VaultInterestCard state={state} update={update} showToast={showToast} />
          <DoubleTokenDayCard state={state} update={update} showToast={showToast} />
        </>
      )}
      {activeCategory === "learning" && <AdaptiveCard state={state} update={update} showToast={showToast} />}
      {activeCategory === "notifications" && (
        <>
          {/* Native Run C, Part 2 (decision 4): three groups of rows. This device — the requests
              switch (today's parent NotificationToggle, same states and handlers), the native-only
              "New features and tips" row, the test link while on — then the household counts, then
              the reminder chips. LB-265's "what you'll get" list folded into the first row's
              sub-line; "On/Off for this device" is what the switch itself says. */}
          <NotificationGroup label={ct("settings.notifications.group_device", "This device")} caption={ct("settings.notifications.device_caption", "These apply to this device only. Turn them on separately on each phone or tablet.")} testId="push-device-group">
            <NotificationToggle
              code={code}
              role="parent"
              childId={null}
              variant="row"
              label={ct("settings.notifications.row_requests", "Requests and finished tasks")}
              sub={ct("settings.notifications.row_requests_sub", "When a child sends a request or finishes their daily tasks")}
              newsRow
              testRow
              onStatusChange={() => setPushRefreshKey((k) => k + 1)}
            />
          </NotificationGroup>
          <HouseholdCoverageCard code={code} state={state} refreshKey={pushRefreshKey} />
          <NotificationGroup label={ct("settings.reminder.title", "Kids' task reminder")} caption={ct("settings.reminder.caption", "One nudge a day, UK time. Only sent to children who have notifications on.")} testId="push-reminder-group">
            <TaskReminderCard state={state} update={update} showToast={showToast} />
          </NotificationGroup>
        </>
      )}
      {activeCategory === "security" && (
        <SecuritySection state={state} update={update} showToast={showToast} verifiedPin={verifiedPin} setPendingVerifiedPin={setPendingVerifiedPin} code={code} />
      )}
      {activeCategory === "danger" && <DangerZoneSection state={state} code={code} showToast={showToast} verifiedPin={verifiedPin} />}
      </SettingsErrorBoundary>
    </SettingsCategoryDetail>
  );
}

// An exception thrown while rendering — or inside a state updater, which is where the
// permission switch threw — unmounts React's whole tree and leaves a white page. The
// screen is gone, there is nothing to tap, and nothing says why: principle 5 in reverse,
// the parent holding the bag for something the app did.
//
// So Settings catches its own. The boundary sits inside the category shell, which means the
// header and its Back button survive: whatever broke, there is always a way off this screen.
// The error text is shown because a parent who reports "it went blank" has told us nothing,
// and one line of it turns an unreproducible report into a fixable one.
class SettingsErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err: (err && err.message) || String(err) }; }
  // Deliberately no telemetry call here: this module does not import the metrics client, and
  // a boundary that can itself throw is worse than one that only renders.
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="settings-error">
        <div className="text-sm font-bold text-slate-800 mb-1">{ct("settings.error_title", "This screen stopped working")}</div>
        <p className="text-xs text-slate-500 mb-3">{ct("settings.error_body", "Nothing has been lost. Go back and open it again, and tell us what you were doing if it keeps happening.")}</p>
        <p className="text-[11px] text-slate-400 font-mono break-words mb-3" data-testid="settings-error-detail">{this.state.err}</p>
        <button
          className="w-full bg-slate-100 text-slate-700 font-bold text-sm rounded-xl py-2.5"
          data-testid="settings-error-back"
          onClick={() => { this.setState({ err: null }); this.props.onBack(); }}
        >{ct("settings.error_back", "Back to Settings")}</button>
      </div>
    );
  }
}

export { TokenSetupFields, AdaptiveCard, DoubleTokenDayCard, VaultInterestCard } from "./settings/Money.jsx";
export { taskReminderHourOf } from "./settings/Notifications.jsx";
